'use client';

import { useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ModalManagerProps {
  isOpen: boolean;
  onClose: () => void;
  contentType: 'privacy' | 'disclaimer';
  title: string;
}

const ModalManager = ({ isOpen, onClose, contentType, title }: ModalManagerProps) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const privacyContent = (
    <div className="space-y-6">
      <section>
        <h3 className="text-xl font-semibold text-text-primary mb-3">Information We Collect</h3>
        <p className="text-text-secondary leading-relaxed">
          LivsmartCoach collects conversation data to provide personalized wellness coaching. We store your chat history during active sessions to maintain context and deliver relevant guidance. No personal health information is shared with third parties without your explicit consent.
        </p>
      </section>
      
      <section>
        <h3 className="text-xl font-semibold text-text-primary mb-3">How We Use Your Data</h3>
        <p className="text-text-secondary leading-relaxed">
          Your conversation data helps our AI understand your wellness goals and provide tailored recommendations. We use this information solely to improve your coaching experience and do not sell or share your data with advertisers or marketing companies.
        </p>
      </section>
      
      <section>
        <h3 className="text-xl font-semibold text-text-primary mb-3">Data Security</h3>
        <p className="text-text-secondary leading-relaxed">
          We implement industry-standard encryption and security measures to protect your information. All conversations are transmitted securely, and session data is automatically cleared when you end your coaching session.
        </p>
      </section>
      
      <section>
        <h3 className="text-xl font-semibold text-text-primary mb-3">Your Rights</h3>
        <p className="text-text-secondary leading-relaxed">
          You have the right to access, modify, or delete your data at any time. Contact our support team if you have questions about your privacy or wish to exercise these rights.
        </p>
      </section>
    </div>
  );

  const disclaimerContent = (
    <div className="space-y-6">
      <section>
        <h3 className="text-xl font-semibold text-text-primary mb-3">Not Medical Advice</h3>
        <p className="text-text-secondary leading-relaxed">
          LivsmartCoach provides general wellness guidance and lifestyle recommendations. Our AI coaching is not a substitute for professional medical advice, diagnosis, or treatment. Always consult qualified healthcare providers for medical concerns.
        </p>
      </section>
      
      <section>
        <h3 className="text-xl font-semibold text-text-primary mb-3">AI-Powered Guidance</h3>
        <p className="text-text-secondary leading-relaxed">
          Our coaching uses artificial intelligence to provide personalized wellness suggestions based on your conversations. While we strive for accuracy, AI recommendations should be considered general guidance rather than definitive health instructions.
        </p>
      </section>
      
      <section>
        <h3 className="text-xl font-semibold text-text-primary mb-3">Emergency Situations</h3>
        <p className="text-text-secondary leading-relaxed">
          If you are experiencing a medical emergency, please contact emergency services immediately. LivsmartCoach is designed for wellness support and lifestyle guidance, not emergency medical situations.
        </p>
      </section>
      
      <section>
        <h3 className="text-xl font-semibold text-text-primary mb-3">Individual Results May Vary</h3>
        <p className="text-text-secondary leading-relaxed">
          Wellness outcomes depend on many factors including individual circumstances, consistency, and personal health conditions. Our recommendations are general in nature and may not be suitable for everyone.
        </p>
      </section>
    </div>
  );

  return (
    <div 
      className="fixed inset-0 z-modal-backdrop flex items-center justify-center p-4 md:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="fixed inset-0 bg-background" aria-hidden="true" />
      
      <div 
        className="relative z-modal bg-card rounded-lg md:rounded-xl shadow-soft-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 md:p-8 border-b border-border">
          <h2 id="modal-title" className="text-2xl md:text-3xl font-semibold text-card-foreground font-heading">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="touch-target flex items-center justify-center rounded-md hover:bg-muted transition-smooth focus-ring"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={24} className="text-text-secondary" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="text-base md:text-lg">
            {contentType === 'privacy' ? privacyContent : disclaimerContent}
          </div>
        </div>
        
        <div className="p-6 md:p-8 border-t border-border">
          <button
            onClick={onClose}
            className="w-full h-12 px-6 bg-primary text-primary-foreground rounded-md font-medium hover:opacity-90 transition-smooth focus-ring"
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalManager;